import { RARITIES, type Rarity } from '@/game/chests';
import { guestValueOf, type Account, type ChestSummary, type GuestValue, type Registration } from '@/net/api';

import { arrivalStore, endArrival, forgetArrivalSeen, showArrival } from './arrival-state';
import { refreshChests } from './chests-state';
import { createStore } from './store';

/**
 * Guest → account: the sign-up moment's state — Blockari's `state/signup.ts`,
 * without React.
 *
 * A guest is a real account (server: `users.is_guest`) that cannot claim a
 * chest or keep a leaderboard name until it registers, and registering upgrades
 * THAT account in place. This module owns when the sign-up sheet may appear and
 * what happens on success; the sheet itself is signup-sheet.ts.
 *
 * Two kinds of "show":
 *   - the player asked (SIGN UP TO CLAIM on a chest, "Keep your rank" on the
 *     rank-up, the leaderboard chip): the sheet opens at once, always, whatever
 *     the cadence;
 *   - the ambient prompt (`promptDue`): at most ONCE PER SESSION and ONCE PER
 *     24 H, on a calm screen — never in play, never over a chest or a
 *     ceremony, never blocking.
 * Either kind counts against the cadence (an asked-for sheet just now is not
 * followed by a nudge). Storage is best-effort: without it the session limit
 * still holds in memory.
 */

export const PROMPT_KEY = 'w21.signup.prompted';
export const PROMPT_GAP_MS = 24 * 60 * 60 * 1000;
/** After the conditions hold this long, the ambient prompt rises (a beat after the screen settles). */
export const PROMPT_DELAY_MS = 9000;
/** The screens the ambient prompt may appear on: calm ones, none of them a game or a result. */
export const PROMPT_SCREENS: readonly string[] = ['start', 'leaderboard', 'profile', 'history'];

let shownThisSession = false;

/** When the sheet last showed (ms since the epoch), or null. Never throws. */
export function lastPrompted(): number | null {
  try {
    const raw = globalThis.localStorage?.getItem(PROMPT_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** May the ambient prompt show now: not yet this session, and 24 h since the last time. */
export function canPrompt(now = Date.now()): boolean {
  if (shownThisSession) return false;
  const last = lastPrompted();
  return last === null || now - last >= PROMPT_GAP_MS;
}

export function markPrompted(now = Date.now()): void {
  shownThisSession = true;
  try {
    globalThis.localStorage?.setItem(PROMPT_KEY, String(now));
  } catch {
    // The session flag still holds.
  }
}

/** Test hook: forget the cadence (memory and storage). */
export function resetPromptCadence(): void {
  shownThisSession = false;
  try {
    globalThis.localStorage?.removeItem(PROMPT_KEY);
  } catch {
    // Nothing to forget.
  }
}

/** Where the sheet was asked from (what it shows first; the ambient one shows what is at stake). */
export type SignupReason = 'chest' | 'rank' | 'board' | 'prompt' | 'keep';

export interface CollectLabelOpts {
  guest: boolean;
  /** The guest already said "Later" on this stage: the rest of its chests are simply kept for later. */
  declined?: boolean;
}

/** The chest reveal's button: COLLECT for an account, SIGN UP TO CLAIM for a guest. */
export function collectLabel({ guest, declined = false }: CollectLabelOpts): string {
  if (!guest) return 'COLLECT';
  return declined ? 'LATER' : 'SIGN UP TO CLAIM';
}

/** The account the UI is showing, as far as the sign-up moment cares. */
export interface Viewer {
  guest: boolean;
  /**
   * A registered account with no password: not a guest (its chests apply, its
   * name is its own), but it can only be reached by its session, so the sheet
   * offers to give it a password before that session ends.
   */
  passwordless: boolean;
  username: string;
  xp: number;
  rank: Account['rank'] | null;
}

export const viewerStore = createStore<Viewer>({ guest: false, passwordless: false, username: '', xp: 0, rank: null });

interface SignupState {
  open: boolean;
  /** The sheet's face: making an account, signing back in to one, or the question before a guest is signed out. */
  mode: 'signup' | 'signin' | 'signout';
  /** The guest being signed out and what it holds, with the call that signs it out anyway (true once done). */
  leave: { value: GuestValue; run: () => Promise<boolean> } | null;
  reason: SignupReason | null;
  /** The sheet was dismissed without an account ("Later", the scrim, Escape): the asker carries on. */
  onLater: (() => void) | null;
  /** The rank the asker is showing (the rank-up's new one: the session may not have caught up yet). */
  rank: Account['rank'] | null;
  /** Registration succeeded: the celebration is on screen (the chests that will arrive after it). */
  celebrating: { name: string; chests: ChestSummary[] } | null;
}

export const signupStore = createStore<SignupState>({
  open: false,
  mode: 'signup',
  leave: null,
  reason: null,
  onLater: null,
  rank: null,
  celebrating: null,
});

/** The player asked to sign up (a chest's button, the rank-up chip, the leaderboard chip). */
export function openSignup(reason: SignupReason, onLater?: () => void, rank?: Account['rank']): void {
  const s = signupStore.getState();
  if (s.open || s.celebrating) return;
  markPrompted();
  signupStore.setState({ open: true, mode: 'signup', reason, onLater: onLater ?? null, rank: rank ?? null });
}

/** The player asked to sign in (the top bar's button, the sheet's "Have an account?"). Never the ambient prompt. */
export function openSignIn(): void {
  const s = signupStore.getState();
  if (s.celebrating) return;
  if (s.open) {
    signupStore.setState({ mode: 'signin' });
    return;
  }
  signupStore.setState({ open: true, mode: 'signin', reason: null, onLater: null, rank: null });
}

const WHOLE = new Intl.NumberFormat('en-US');

/**
 * What a guest stands to lose by signing in to an account, in the player's words: exact amounts, in the order they
 * weigh. ("1,340 $CHAIN", "2 tickets", "a paid seat in play", "3 unopened chests".) Empty when it holds nothing.
 */
export function guestLossParts(value: GuestValue): string[] {
  const parts: string[] = [];
  if (value.balance > 0) parts.push(`${WHOLE.format(value.balance)} $CHAIN`);
  if (value.tickets > 0) parts.push(value.tickets === 1 ? '1 ticket' : `${WHOLE.format(value.tickets)} tickets`);
  if (value.seats > 0) parts.push(value.seats === 1 ? 'a paid seat in play' : `${value.seats} paid seats in play`);
  if (value.chests > 0) parts.push(value.chests === 1 ? '1 unopened chest' : `${value.chests} unopened chests`);
  return parts;
}

/** "A", "A and B", "A, B and C". */
export function listWords(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** The other face of the open sheet: "Have an account? Sign in" / "New here? Create account". */
export function switchSheet(mode: 'signup' | 'signin'): void {
  if (signupStore.getState().open) signupStore.setState({ mode, leave: null });
}

/**
 * "Register this guest instead": from the question asked before a guest is thrown away (signing out, or logging in
 * over it), to the sign-up sheet, titled for what it is doing ("Keep this guest").
 */
export function registerInstead(): void {
  if (signupStore.getState().open) signupStore.setState({ mode: 'signup', leave: null, reason: 'keep' });
}

/**
 * What to do with the server's answer to a sign-out. A guest that holds value is refused (409 `guest-has-value`): ask
 * first. Anything else carries on exactly as sign-out always has (a registered account, a guest with nothing, even a
 * call that never got an answer: the browser goes back to being a guest rather than stuck on an account it asked to
 * leave).
 */
export function signOutVerdict(result: Parameters<typeof guestValueOf>[0]): { kind: 'ask'; value: GuestValue } | { kind: 'done' } {
  const value = guestValueOf(result);
  return value ? { kind: 'ask', value } : { kind: 'done' };
}

/** Ask before a guest with value is signed out. `run` signs it out anyway (true once done). */
export function openSignOutConfirm(value: GuestValue, run: () => Promise<boolean>): void {
  const s = signupStore.getState();
  if (s.celebrating) return;
  signupStore.setState({ open: true, mode: 'signout', leave: { value, run }, reason: null, onLater: null, rank: null });
}

/** Dismiss. One call, one tap: "Later" is never more than that. */
export function dismissSignup(): void {
  const cb = signupStore.getState().onLater;
  signupStore.setState({ open: false, mode: 'signup', leave: null, reason: null, onLater: null, rank: null });
  cb?.();
}

export function finishCelebration(): void {
  signupStore.setState({ celebrating: null });
}

export interface PromptContext {
  guest: boolean;
  screen: string;
  /** A chest's arrival / reveal is on screen. */
  arrivalOpen: boolean;
  /** The sheet (or its celebration) is up already. */
  sheetOpen: boolean;
  /** Something to keep: XP earned, or a chest waiting. */
  hasStake: boolean;
}

/** Whether the ambient prompt may be scheduled now (the cadence is checked separately, at the moment it would show). */
export function promptDue(p: PromptContext): boolean {
  return p.guest && p.hasStake && !p.arrivalOpen && !p.sheetOpen && PROMPT_SCREENS.includes(p.screen);
}

/** How long the celebration holds before the chests arrive. */
export const CELEBRATE_MS = 1500;

/** What the app does when an account changes under the sheet (main.ts wires it). */
export interface SignupHooks {
  /** The session has a new name (the account is the same one). */
  accountChanged(user: Account): void;
  /** Signed in to an existing account: the app takes it as the session's account. */
  signedIn?(user: Account): void;
  /** A reveal stage that is not an arrival (opened from the inbox) is up: close it. */
  closeReveal?(): void;
}

let hooks: SignupHooks = { accountChanged: () => undefined };

export function registerSignupHooks(next: SignupHooks): void {
  hooks = next;
}

/**
 * Registration succeeded (the sheet's). The account is the same one, so the
 * session just learns its new name; the chests that were held are ordinary
 * chests now, each owed its arrival again (it is where they are opened), plus
 * the welcome gift. The celebration plays first (signup-sheet.ts), then
 * `arriveAfterCelebration` puts the arrival on screen.
 */
export function afterRegistration(reg: Registration): void {
  hooks.accountChanged(reg.user);
  // The released chests arrived once already (as a guest's, locked): they arrive again, unlocked.
  forgetArrivalSeen(reg.released.map((c) => c.id));
  const chests = [...reg.released, ...(reg.welcome ? [reg.welcome] : [])];
  void refreshChests();
  signupStore.setState({
    open: false,
    reason: null,
    onLater: null,
    rank: null,
    celebrating: { name: reg.user.username, chests },
  });
}

/**
 * Signed back in (the sheet's). A different account from the one this browser held: the app swaps it
 * in whole (its balance, XP, chests, feed), and the sheet is done. No celebration: nothing was earned.
 */
export function afterSignIn(user: Account): void {
  signupStore.setState({ open: false, mode: 'signup', leave: null, reason: null, onLater: null, rank: null });
  hooks.signedIn?.(user);
}

/** The celebration is over: whatever chest stage was up (the locked one) gives way to the unlocked arrival. */
export function arriveAfterCelebration(chests: readonly ChestSummary[]): boolean {
  if (arrivalStore.getState().batch) endArrival();
  hooks.closeReveal?.();
  return showArrival(chests);
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** The time left shows only once it is short: an honest nudge, never a countdown from day one. */
export const URGENT_DAYS = 3;

/** The best of a batch of chests, as it will land (the highest starting rarity). */
export function bestRarity(chests: readonly Pick<ChestSummary, 'startRarity'>[]): Rarity {
  let best = 0;
  for (const c of chests) best = Math.max(best, RARITIES.indexOf(c.startRarity));
  return RARITIES[best] ?? 'common';
}

/** Whole days until the soonest expiry among these chests, or null when none has one. */
export function daysLeft(chests: readonly Pick<ChestSummary, 'expiresAt'>[], now = Date.now()): number | null {
  let soonest: number | null = null;
  for (const c of chests) {
    if (!c.expiresAt) continue;
    const t = Date.parse(c.expiresAt);
    if (Number.isFinite(t) && (soonest === null || t < soonest)) soonest = t;
  }
  return soonest === null ? null : Math.max(0, Math.ceil((soonest - now) / DAY_MS));
}

/**
 * The sheet offers a friendly free name by default; the guest's own
 * (`guest_ab12cd`) stays one tap away as "Keep guest_ab12cd", shown while the
 * field says something else.
 */
export function keepNameOffer(typed: string, guestName: string): string | null {
  return typed !== guestName && guestName.length > 0 ? guestName : null;
}
